import { CircularProgress, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FC, useEffect, useState } from 'react'
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ContainedButton, OutlinedButton } from '../styled';
import ShowCodeBlock from '../../../components/codeBlock';
import { codeReviewReport, codeToDocument } from '../../../services/document.service';
import { useDispatch } from 'react-redux';
import { incrementDocumentationStep, decrementDocumentationStep } from '../../../store/documentSlice';
import './style.scss'
import SuggestionDisplay from '../../../components/SuggestionDisplay';

interface ConvertedContent {
  [key: string]: string;
}

interface BugsAndErrorReportProps {
  bugsAndErrorHandlingReport: any
  jsonConvertedContent: ConvertedContent
  setJsonConvertedContent: React.Dispatch<React.SetStateAction<any>>
  reportFeedback: any
  setReportFeedback: React.Dispatch<React.SetStateAction<any>>
  requiredIssueDetails: any
  savedFile: any
  setCoreFunctionalities: React.Dispatch<React.SetStateAction<any>>
}

const BugsAndErrorReport: FC<BugsAndErrorReportProps> = ({ bugsAndErrorHandlingReport, jsonConvertedContent,
  setJsonConvertedContent,
  reportFeedback,
  setReportFeedback,
  requiredIssueDetails,
  savedFile,
  setCoreFunctionalities
}) => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  function convertToJSON(inputString: string): any {
    // Split into sections based on double asterisks for titles
    const sections = inputString?.split(/```/g); // Split by backticks for code blocks
    const result: any = {};
    let counter = 1; // Section counter for dynamic titles

    for (let i = 0; i < sections?.length; i++) {
      const section = sections[i].trim();
      if (!section) continue; // Skip empty sections

      if (i % 2 === 1) {
        // Code block (odd sections after split)
        const title = `Code Block ${counter}`;
        result[title] = { type: "code", content: section };
        counter++;
      } else {
        // Text/explanation block (even sections after split)
        const title = `Text Block ${counter}`;
        result[title] = { type: "text", content: section };
      }
    }

    return result;

  }

  const getLanguage = (value: string) => {
    return value?.replace('```', "")?.split("\n")[0]
  }

  const jsonToHTML = (data: any): string => {
    if (typeof data === 'string') {
      // Convert newlines to <br/>
      data = data.replace(/\n/g, '<br/>');

      // Handle bold text (surrounded by **)
      data = data.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Handle code blocks (triple backticks)
      data = data.replace(/```(.*?)```/g, '<pre class="language-markup"><code>$1</code></pre>');

      // Handle headings (for example, **Retrieving Public UUID:** becomes <h2>)
      if (/^\*\*/.test(data)) {
        data = `<h2>${data.replace(/\*\*(.*?)\*\*/g, '$1')}</h2>`;
      }

      return `<p>${data}</p>`;
    }

    if (Array.isArray(data)) {
      return `<ul>${data.map(item => `<li>${jsonToHTML(item)}</li>`).join('')}</ul>`;
    }

    if (typeof data === 'object' && data !== null) {
      return Object.entries(data)
        .map(([key, value]) => {
          return `<div><strong>${key}:</strong> ${jsonToHTML(value)}</div>`;
        })
        .join('');
    }

    return '';
  };

  const replacedValue = (value: string) => {
    return value.replace(value?.substring(0, value.indexOf("\n")), "").replace("```", "")
  };

  const getDisabledStatus = (): boolean => {
    let isValid = true
    Object.values(reportFeedback || {}).forEach((value: any) => {
      if (value.selection === "Fix-not-required" && !value.reason) {
        isValid = false
      }
    })

    if (isValid) {
      isValid = Object.keys(reportFeedback || {})?.length === jsonConvertedContent?.issues?.length
    }

    return !isValid
  }

  const handleSaveReport = async () => {
    try {
      setLoading(true);
      const reportFeedbackClone = { ...reportFeedback }
      reportFeedbackClone["issueDetails"] = requiredIssueDetails
      const codeToDocRes = await Promise.all([codeToDocument(savedFile), codeReviewReport({ feedback: reportFeedbackClone, review_id: bugsAndErrorHandlingReport?.review_id || "" })])
      if (codeToDocRes[0]) {
        setCoreFunctionalities(codeToDocRes[0])
        dispatch(incrementDocumentationStep())
      }
    } catch (e) {
      console.error("error in save report", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (Object.keys(bugsAndErrorHandlingReport || {})?.length) {
      setJsonConvertedContent({ ...bugsAndErrorHandlingReport, "snippets": convertToJSON(bugsAndErrorHandlingReport["snippets"]) })
    }

  }, [bugsAndErrorHandlingReport]);

  useEffect(() => {
    return () => {
      setLoading(false)
    }
  }, [])

  return (
    <div className='report-container'>
      <DialogTitle className='bugsAndReport-head'>Exception handling and Bugs Report</DialogTitle>
      <DialogContent>
        {Object.entries(jsonConvertedContent || {}).map(([parentKey, parentValue], index) => {
          return parentKey === "issues" ? <div key={index} className='issue-container'>
            {Array.isArray(parentValue) && parentValue?.map((value: any, valIndex: number) => {
              return <div className='each-report-container' key={value?.issue + valIndex }>
                <div className='report-key-container'>{valIndex + 1}. {value?.issue}</div>
                <SuggestionDisplay suggestion={value?.suggestion}/>
                <div className='report-action-container'>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    className='report-action-container'
                    value={reportFeedback[value?.issue]?.selection || ""}
                    onChange={(e: any) => setReportFeedback((prev: any) => ({ ...prev, [value?.issue]: { selection: e.target.value, reason: "" } }))}
                  >
                    <div className="each-radio-container">
                      <FormControlLabel
                        value="Fixed"
                        control={<Radio
                          disableRipple
                          sx={{
                            '&, &.Mui-checked': {
                              color: '#43425D',
                            }
                          }} />}
                        label="Fixed"
                      />
                    </div>
                    <div className="each-radio-container">
                      <FormControlLabel
                        value="Fix-not-required"
                        control={<Radio
                          disableRipple
                          sx={{
                            '&, &.Mui-checked': {
                              color: '#43425D',
                            }
                          }} />}
                        label="Fix not required"
                      />
                      {reportFeedback[value?.issue]?.selection === "Fix-not-required" && <div className='justification-input-container'>
                        <input
                          className={"justification-input"}
                          type='text'
                          value={reportFeedback[value?.issue]?.reason}
                          onChange={(e) => setReportFeedback((prev: any) => ({ ...prev, [value?.issue]: { selection: reportFeedback[value?.issue]?.selection, reason: e.target.value } }))}
                          placeholder='Justify the reason that the fix is not required'
                        />
                      </div>}
                    </div>
                    <div className="each-radio-container">
                      <FormControlLabel
                        value="Purposed-solution-doesn't-work"
                        control={<Radio
                          disableRipple
                          sx={{
                            '&, &.Mui-checked': {
                              color: '#43425D',
                            }
                          }} />}
                        label="Purposed solution doesn't work"
                      />
                      {reportFeedback[value?.issue]?.selection === "Purposed-solution-doesn't-work" && <div className='justification-input-container'>
                        <input
                          className={"justification-input"}
                          type='text'
                          value={reportFeedback[value?.issue]?.reason}
                          onChange={(e) => setReportFeedback((prev: any) => ({ ...prev, [value?.issue]: { selection: reportFeedback[value?.issue]?.selection, reason: e.target.value } }))}
                          placeholder='Justify your selection'
                        />
                      </div>}
                    </div>
                    <div className="each-radio-container">
                      <FormControlLabel
                        value="Purposed-solution-works-but-it-creates-more-issues"
                        control={<Radio
                          disableRipple
                          sx={{
                            '&, &.Mui-checked': {
                              color: '#43425D',
                            }
                          }} />}
                        label="Purposed solution works but it creates more issues"
                      />
                      {reportFeedback[value?.issue]?.selection === "Purposed-solution-works-but-it-creates-more-issues" && <div className='justification-input-container'>
                        <input
                          className={"justification-input"}
                          type='text'
                          value={reportFeedback[value?.issue]?.reason}
                          onChange={(e) => setReportFeedback((prev: any) => ({ ...prev, [value?.issue]: { selection: reportFeedback[value?.issue]?.selection, reason: e.target.value } }))}
                          placeholder='Justify your selection'
                        />
                      </div>}
                    </div>
                    <div className="each-radio-container">
                      <FormControlLabel
                        value="There-is-a-better-solution"
                        control={<Radio
                          disableRipple
                          sx={{
                            '&, &.Mui-checked': {
                              color: '#43425D',
                            }
                          }} />}
                        label="There is a better solution"
                      />
                      {reportFeedback[value?.issue]?.selection === "There-is-a-better-solution" && <div className='justification-input-container'>
                        <input
                          className={"justification-input"}
                          type='text'
                          value={reportFeedback[value?.issue]?.reason}
                          onChange={(e) => setReportFeedback((prev: any) => ({ ...prev, [value?.issue]: { selection: reportFeedback[value?.issue]?.selection, reason: e.target.value } }))}
                          placeholder='Justify your selection'
                        />
                      </div>}
                    </div>
                  </RadioGroup>
                </div>
              </div>
            })}

          </div> : parentKey === "snippets" ? <div className='code-snippet-container'>
            <div className='snippet-header'>
              Fix with Snippets:
            </div>
            {(Object.values(parentValue || {}).map((value: any) => {
              return <div className='each-report-container'>
                <div className='code-value'>{value.type === "code" ? <ShowCodeBlock text={replacedValue(value.content)} languageType={getLanguage(value.content)} /> : <ul>
                  {value?.content?.split("-")?.map((content: string) => {
                    if (content) {
                      return <li>{content}</li>
                    }
                  })}
                </ul>}</div>
              </div>
            }))}
          </div> : <div></div>
        }
        )
        }
      </DialogContent >
      <DialogActions>
        <div className='dialog-action-container'>
          <OutlinedButton onClick={() => dispatch(decrementDocumentationStep())}>
            Back
          </OutlinedButton>
          <ContainedButton
            disabled={getDisabledStatus()}
            onClick={handleSaveReport}
          >
            {loading ? <CircularProgress style={{ color: 'white', marginLeft: '5px' }} size='1rem' /> :
              'Proceed'}
          </ContainedButton>
        </div>
      </DialogActions>
    </div >
  );
}

export default BugsAndErrorReport;
